import { config } from '../config';

const API_BASE_URL = config.API_URL;

export interface User {
  id: string;
  username: string;
  email: string;
  is_admin: boolean;
}

export interface Album {
  id: string;
  name: string;
  description: string;
  created_at: string;
  photo_count: number;
  is_public: boolean;
  owner_id: string;
}

export interface Media {
  id: string;
  filename: string;
  file_type: string;
  s3_key: string;
  url: string;
  thumbnail_url?: string;
  size: number;
  uploaded_at: string;
  album_id: string;
}

export interface PaginationInfo {
  page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface AlbumsResponse {
  albums: Album[];
}

export interface MediaResponse {
  media: Media[];
  pagination: PaginationInfo;
}

class ApiService {
  private token: string | null = null;

  setToken(token: string) {
    this.token = token;
  }

  clearToken() {
    this.token = null;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Authentication
  async login(username: string, password: string) {
    return this.request<{
      message: string;
      requires_otp: boolean;
      user_id?: string;
      email?: string;
      token?: string;
      user?: User;
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async verifyOtp(userId: string, otp: string) {
    return this.request<{ message: string; token: string; user: User }>('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, otp }),
    });
  }

  async resendOtp(userId: string) {
    return this.request<{ message: string }>('/api/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async getCurrentUser() {
    return this.request<{ id: string; username: string; email: string; is_admin: boolean }>('/api/auth/me');
  }

  // Albums
  async getAlbums(): Promise<AlbumsResponse> {
    return this.request<AlbumsResponse>('/api/albums');
  }

  // Media
  async getAlbumMedia(albumId: string, page: number = 1, perPage: number = 30): Promise<MediaResponse> {
    return this.request<MediaResponse>(`/api/albums/${albumId}/media?page=${page}&per_page=${perPage}`);
  }
}

export const apiService = new ApiService();
